
import { useState, useEffect, useCallback } from "react";
import { Heart, Bookmark, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { SocialActions } from "@/components/social/SocialActions";
import { socialApi } from "@/api/social";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { CommentsDialog } from "./CommentsDialog";
import { formatNumber } from "@/utils/dataUtils/formatters";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";

interface DerivativeSocialActionsProps {
  derivativeId: string;
  showComments?: boolean;
  initialCounts?: {
    likes_count?: number;
    favorites_count?: number;
    comments_count?: number;
  };
  onOpenComments?: () => void;
  size?: "xs" | "sm" | "default" | "lg";
  className?: string;
  orientation?: "horizontal" | "vertical";
  disableCommentsDialog?: boolean; // Added prop to disable comments dialog
}

// Global cache to store social counts across components
interface SocialCountsCache {
  [key: string]: {
    data: {
      likes_count: number;
      favorites_count: number;
      comments_count: number;
    };
    timestamp: number;
  };
}

// In-memory cache with 10-minute expiration
const socialCountsCache: SocialCountsCache = {};
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

export const DerivativeSocialActions = ({
  derivativeId,
  showComments = true,
  initialCounts,
  onOpenComments,
  size = "xs", // Changed default to xs for smaller buttons
  className,
  orientation = "horizontal",
  disableCommentsDialog = false
}: DerivativeSocialActionsProps) => {
  // State for comments dialog
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  
  // Debug the incoming initialCounts
  useEffect(() => {
    console.log(`DerivativeSocialActions (${derivativeId}) - initialCounts:`, initialCounts);
  }, [derivativeId, initialCounts]);
  
  // Use initial counts if provided to render immediately without loading state
  const hasInitialCounts = initialCounts && (
    initialCounts.likes_count !== undefined ||
    initialCounts.favorites_count !== undefined ||
    initialCounts.comments_count !== undefined
  );

  // Ensure initial counts are never negative or undefined
  const safeInitialCounts = initialCounts ? {
    likes_count: Math.max(0, initialCounts.likes_count || 0),
    favorites_count: Math.max(0, initialCounts.favorites_count || 0),
    comments_count: Math.max(0, initialCounts.comments_count || 0)
  } : undefined;

  // Check cache first
  const cachedData = socialCountsCache[derivativeId];
  const isCacheValid = cachedData && (Date.now() - cachedData.timestamp < CACHE_DURATION);
  
  // Always update cache with initial counts if provided
  useEffect(() => {
    if (safeInitialCounts) {
      socialCountsCache[derivativeId] = {
        data: safeInitialCounts,
        timestamp: Date.now()
      };
      console.log(`Updated cache for ${derivativeId}:`, safeInitialCounts);
    }
  }, [derivativeId, safeInitialCounts]);
  
  // OPTIMIZATION: Show buttons immediately with initial or cached data
  const [isLoading, setIsLoading] = useState(false);
  const [counts, setCounts] = useState(safeInitialCounts || 
    (isCacheValid ? cachedData.data : {
      likes_count: 0,
      favorites_count: 0,
      comments_count: 0
    })
  );

  // Handle opening comments dialog
  const handleOpenComments = () => {
    if (onOpenComments) {
      onOpenComments();
    } else {
      setIsCommentsOpen(true);
    }
  };

  // Fetch social counts if we don't have initial counts - but do it in background
  const fetchCounts = useCallback(async () => {
    // Skip fetch if we have initial counts or valid cache
    if (hasInitialCounts || isCacheValid) return;
    
    try {
      const data = await socialApi.getSocialCounts(derivativeId);
      
      // Ensure counts are never negative
      const safeCounts = {
        likes_count: Math.max(0, data.likes_count || 0),
        favorites_count: Math.max(0, data.favorites_count || 0),
        comments_count: Math.max(0, data.comments_count || 0)
      };
      
      console.log(`Fetched counts for ${derivativeId}:`, safeCounts);
      
      // Update state and cache
      setCounts(safeCounts);
      socialCountsCache[derivativeId] = {
        data: safeCounts,
        timestamp: Date.now()
      };
    } catch (error) {
      console.error(`Error fetching social counts for ${derivativeId}:`, error);
    } finally {
      setIsLoading(false);
    }
  }, [derivativeId, hasInitialCounts, isCacheValid]);

  // Use effect to fetch counts when component mounts or derivativeId changes
  // OPTIMIZATION: Use setTimeout to defer the fetch until after initial render
  useEffect(() => {
    if (derivativeId && !hasInitialCounts && !isCacheValid) {
      // Use a small timeout to prioritize UI rendering first
      const timer = setTimeout(() => {
        fetchCounts();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [derivativeId, fetchCounts, hasInitialCounts, isCacheValid]);

  // Pass through to the full SocialActions component - always render immediately
  return (
    <ErrorBoundary>
      <div className={cn("transition-opacity", className)}>
        <SocialActions 
          derivativeId={derivativeId}
          size={size}
          showComments={showComments}
          onOpenComments={handleOpenComments}
          className={className}
          orientation={orientation}
          initialCounts={counts}
          disableCommentsDialog={disableCommentsDialog}
        />
      </div>
      
      {/* Comments dialog component - conditionally render based on disableCommentsDialog */}
      {showComments && !disableCommentsDialog && (
        <CommentsDialog 
          derivativeId={derivativeId}
          isOpen={isCommentsOpen}
          onOpenChange={setIsCommentsOpen}
        />
      )}
    </ErrorBoundary>
  );
};
